import format from 'date-fns/format';

import React, { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useMutation, useQuery } from 'react-query';
import useLocalStorage from 'use-local-storage';
import ContactForm from '../components/ContactForm';
import MessageAlreadySent from '../components/MessageAlreadySent';
import MessageSent from '../components/MessageSent';
import Proposition from '../components/Proposition';
import Alert from '../components/UI/Alert';
import Title from '../components/UI/Title';
import { checkMail, sendMail } from '../services/mail';
import { renderSpan } from '../services/renderValues';
import ContactFormType from '../types/ContactFormType';

type MailResponse = { message: string; status: number } | null;
/**
 * ## Contact form
 *
 * We first call the backend to check if user has already sent a mail today.
 * Also we use localStorage to avoid ip switch.
 * ### Rules to follow
 * - User can only send one mail per day
 * - If Backend is not responding : display error message
 * - If a user has already sent a mail : display a message and hide form.
 * - If a user send a mail : display success message (or error message, if server fails)
 * - If the age or the time used to send email is improper : display message
 *
 * @returns {JSX.Element}
 */
function Contact() {
  const [timer, setTimer] = useState(0);
  const [lastMail, setLastMail] = useLocalStorage<string | null>(
    'lastMail',
    null,
  );
  const [mailSentResponse, setMailSentResponse] = useState<MailResponse>(null);

  const {
    data, isSuccess, isError, isLoading,
  } = useQuery(
    'checkMail',
    checkMail,
    {
      retryDelay: 1000,
      staleTime: 10000,
    },
  );
  const { mutate } = useMutation((mail: FormData) => sendMail(mail), {
    onSuccess: async (apiData) => {
      const msg = await apiData.json();
      setMailSentResponse({ status: apiData.status, message: msg.message });
      if (apiData.status !== 200) {
        return;
      }
      setLastMail(format(new Date(), 'dd-MM-yyyy'));
    },
    onError: (err) => {
      console.log('error :>> ', err);
    },
  });

  useEffect(() => {
    const interval = setInterval(() => setTimer(timer + 1), 1000);
    return () => clearInterval(interval);
  });

  const onSubmit: SubmitHandler<ContactFormType> = (formData) => {
    const bodyFromData = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      bodyFromData.append(key, value);
    });
    bodyFromData.append('timer', timer.toString());
    mutate(bodyFromData);
  };

  // todo change this to get a variable, why is this a fonction for god sake?
  function wasMailSentToday() {
    return lastMail === format(new Date(), 'dd-MM-yyyy');
  }

  return (
    <>
      <section className="text-left text-sm-center">
        <Title titleSection level={1}>
          <FormattedMessage
            id="contact.title"
            values={
              {
                span: renderSpan,
              } as Record<string, any>
            }
          />
        </Title>
      </section>

      <main>
        <div className="container">
          <div className="row">
            <Proposition />

            <section className="col-12 col-lg-8 col-xl-8">
              <div className="row">
                <div className="col-12">
                  <MessageSent response={mailSentResponse} />

                  {/* We check that the user has not already send a mail (localstorage & Api) */}
                  {!isError && data && mailSentResponse?.status !== 200 && (
                    <>
                      <MessageAlreadySent
                        isSuccess={isSuccess}
                        inLocalStorage={wasMailSentToday()}
                        message={data.message}
                      />

                      {isSuccess
                        && data.message.length === 0
                        && !wasMailSentToday() && (
                          <ContactForm onSubmit={onSubmit} />
                      )}
                    </>
                  )}
                  {isLoading && (
                    <Alert>
                      <FormattedMessage id="loading" />
                    </Alert>
                  )}
                  {isError && (
                    <Alert>
                      <FormattedMessage id="contact.error-form-loading" />
                    </Alert>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </>
  );
}

export default Contact;
